import { useTranslation } from "react-i18next";

import { usePublicDiscounts, type PublicQuantityTier } from "@/api/queries/publicDiscounts";
import { cx } from "@/lib/utils";

interface Props {
  /** Currently picked quantity. The chip whose range covers this
   *  number renders selected. */
  quantity: number;
  /** Material code the configurator is set to. We prefer per-material
   *  tiers when present and fall back to the global default lane
   *  (``material_code === null``). */
  materialCode: string;
  /** Click → set the quantity to the chip's lower bound. */
  onPick: (qty: number) => void;
}

/**
 * Discount-tier chip row shown above the quantity stepper.
 *
 * Renders one pill per active tier with the qty range and discount
 * percent. Selecting a chip jumps the stepper to the chip's lower
 * bound — the quickest way for a customer to "land in the next
 * discount bracket". Hidden when no tiers are loaded so the
 * stepper isn't surrounded by an empty bar.
 */
export function QuantityTierChips({
  quantity,
  materialCode,
  onPick,
}: Props): JSX.Element | null {
  const { t } = useTranslation("quote");
  const { data } = usePublicDiscounts();

  const tiers = pickTiers(data?.quantity_tiers ?? [], materialCode);
  if (tiers.length === 0) return null;
  // KAN-2189 — zvýrazněné je JEDNO pásmo: to, které použije cena. Engine bere
  // první pásmo (podle ``min_qty``), do kterého počet padne; překrývající se
  // pásma tenanta svítila naráz dvě.
  const activeTier = tiers.find((tier) => isActive(tier, quantity)) ?? null;

  return (
    <div
      className="-mt-1 flex flex-wrap gap-1.5"
      data-testid="quantity-tier-chips"
    >
      {tiers.map((tier) => {
        const active = tier === activeTier;
        const label =
          tier.max_qty === null
            ? t("config.tierFromOnly", { min: tier.min_qty })
            : t("config.tierFromTo", {
                min: tier.min_qty,
                max: tier.max_qty,
              });
        return (
          <button
            type="button"
            key={`${tier.min_qty}-${tier.max_qty ?? "inf"}`}
            onClick={() => onPick(tier.min_qty)}
            aria-pressed={active}
            data-testid={`tier-${tier.min_qty}`}
            className={cx(
              "flex min-w-[64px] flex-col items-center rounded-md border px-2 py-1.5 text-[11px] transition",
              active
                ? "border-accent-border bg-accent-bg text-accent-ink"
                : "border-border bg-surface text-ink-2 hover:border-border-strong",
            )}
          >
            <span className="font-medium">{label}</span>
            <span className={cx("mono text-[10px] tabular-nums", active ? "text-accent-ink" : "text-ink-3")}>
              {tier.discount_pct > 0 ? `−${tier.discount_pct}%` : "—"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Pick the active tier ladder for a given material, preferring
 * per-material overrides over the ``material_code === null`` global
 * default. Returns an empty array when neither lane has rows.
 */
function pickTiers(
  all: PublicQuantityTier[],
  materialCode: string,
): PublicQuantityTier[] {
  const byMaterial = all
    .filter((t) => t.material_code === materialCode)
    .sort((a, b) => a.min_qty - b.min_qty);
  if (byMaterial.length > 0) return byMaterial;
  return all
    .filter((t) => t.material_code === null)
    .sort((a, b) => a.min_qty - b.min_qty);
}

function isActive(tier: PublicQuantityTier, qty: number): boolean {
  if (qty < tier.min_qty) return false;
  if (tier.max_qty !== null && qty > tier.max_qty) return false;
  return true;
}
